import { useState } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { RelativeTime } from './relative-time';
import type { AuditEvent } from '@/schemas/licenses';
import type { TFunction } from 'i18next';

interface LicenseActivityTimelineProps {
  events: AuditEvent[];
  total: number;
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
  degraded?: boolean;
}

const EVENT_VERB_CLASS: Record<string, string> = {
  created: 'text-status-active',
  activated: 'text-status-active',
  activation: 'text-status-active',
  updated: 'text-status-pending',
  revoked: 'text-status-revoked',
  deactivated: 'text-status-expired',
};

function eventVerb(event: AuditEvent, t: TFunction): string {
  const eventKey = `common.events.${event.event.toLowerCase()}`;
  const translated = t(eventKey);
  return translated === eventKey ? event.event : translated;
}

export function LicenseActivityTimeline({
  events,
  total,
  page,
  pages,
  onPageChange,
  degraded,
}: LicenseActivityTimelineProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section>
      <h2 className="mb-5 text-xl font-semibold tracking-tight text-foreground">
        {t('licenses.detail.activity.title')}
      </h2>

      {degraded && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-status-pending/30 bg-status-pending/10 px-3 py-2 text-[13px] text-status-pending">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('licenses.detail.activity.degraded')}</span>
        </div>
      )}

      <ul className="border-t">
        {events.map((evt) => {
          const isExpanded = expanded.has(evt.id);
          const verb = eventVerb(evt, t);
          const verbClass = EVENT_VERB_CLASS[evt.event.toLowerCase()] ?? 'text-muted-foreground';
          return (
            <li key={evt.id} className="border-b">
              <button
                className="flex w-full items-baseline justify-between gap-4 rounded-sm px-1 py-3 text-left transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => toggle(evt.id)}
                aria-expanded={isExpanded}
              >
                <span className="min-w-0 text-[14px]">
                  <span className={`font-medium capitalize ${verbClass}`}>{verb}</span>{' '}
                  <span className="text-muted-foreground">
                    {t('licenses.detail.activity.fromBy', {
                      ip: evt.ipAddress ?? t('licenses.detail.activity.unknownIp'),
                      actor: evt.performedBy,
                    })}
                  </span>
                </span>
                <span className="whitespace-nowrap text-[13px] text-faint">
                  <RelativeTime date={evt.timestamp} fallback="—" />
                </span>
              </button>
              {isExpanded && (
                <pre className="mx-1 mb-3 max-h-48 overflow-auto rounded-md border bg-foreground/[0.02] p-3 font-mono text-xs">
                  {JSON.stringify(evt.metadata ?? {}, null, 2)}
                </pre>
              )}
            </li>
          );
        })}
      </ul>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[13px] tabular-nums text-faint">
            {t('common.pageOf', { page, pages, total })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              aria-label={t('common.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pages}
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
